import { Type, type Static } from "@sinclair/typebox";
import { z } from "zod";
import type { JsonWireValue } from "./json-value.ts";
import { MatrixEventIdSchema } from "./agent-host.ts";
import { UserAutomationExecutionEnvironmentSchema } from "./user-automation.ts";

export const BROWSER_EXPERT_AGENT_ID = "ccb21146-7b74-413b-96e8-33ffa8cd986c" as const;
export const BROWSER_EXPERT_AGENT_KEY = "browser-expert" as const;
export const BROWSER_EXPERT_DISPLAY_NAME = "浏览器专家" as const;
export const BROWSER_EXPERT_DESCRIPTION =
  "帮你把网页上的日常操作变成可重复执行的工作。" as const;
export const BROWSER_EXPERT_MATRIX_LOCALPART = "browser-expert" as const;
export const BROWSER_EXPERT_INVOCATION_CONTENT_KEY =
  "io.matrix_idp.browser_expert.invocation" as const;

export const BROWSER_EXPERT_MODES = ["program_authoring", "conversation_execution"] as const;
export const BROWSER_EXPERT_TOOLS = [
  "open_browser",
  "read_recorded_step_evidence",
  "suggest_frontend_program",
  "check_frontend_program",
  "request_user_confirmation",
  "test_frontend_program",
  "save_frontend_tool",
  "list_frontend_tools",
  "read_frontend_tool",
  "request_user_input",
  "execute_frontend_tool",
  "delete_frontend_tool"
] as const;
export const BROWSER_EXPERT_AUTOMATIC_TOOLS = [
  "list_frontend_tools",
  "execute_frontend_tool",
  "finish_without_execution"
] as const;
export const BROWSER_PROGRAM_SOURCE_MAX_CHARS = 48_000;

export const FRONTEND_TOOL_REALTIME_EVENTS = Object.freeze({
  automationCommand: "frontend_tool_automation_command",
  automationReply: "frontend_tool_automation_reply",
  probe: "frontend_tool_probe",
  ready: "frontend_tool_ready",
  request: "frontend_tool_request",
  response: "frontend_tool_response",
  cancel: "frontend_tool_cancel"
} as const);
export const BROWSER_CONTROL_DEVICE_TOPIC_PREFIX = "browser-control:device:" as const;
export const BROWSER_CONTROL_AGENT_TOPIC_PREFIX = "browser-control:agent:" as const;

const SourceEventId = z.string().min(4).max(512).regex(/^\$\S+$/u);
const InvocationId = z.uuid();
const BrowserControlRoomId = z.string().min(2).max(512).regex(/^!\S+$/u);
const BrowserControlReplyTopic = z.string().min(1).max(512).regex(/^browser-control:agent:\S+$/u);
const VFS_PATH_PATTERN = "^/[^\\u0000-\\u001F\\u007F\\\\]+$";
const PROGRAM_PATH_PATTERN = "^/[^\\u0000-\\u001F\\u007F\\\\]+\\.flow\\.ts$";
const SAVABLE_PROGRAM_PATH_PATTERN =
  "^/(?:input|working_home|tmp/files|tmp/output)/[^\\u0000-\\u001F\\u007F\\\\]+\\.flow\\.ts$";
// Frontend Tool DSL `name` (toolId) pattern. Keep in sync with FRONTEND_TOOL_ID_PATTERN
// in packages/frontend-tool/src/domain/frontend-tool-schema.ts.
const FRONTEND_TOOL_ID_PATTERN = "^[a-z][a-z0-9]*(?:\\.[a-z][a-z0-9-]*)+$";
export const BrowserExpertModeSchema = z.enum(BROWSER_EXPERT_MODES);
export const BrowserExpertInvocationContentSchema = Type.Object(
  {
    version: Type.Literal("1.0"),
    mode: Type.Union(BROWSER_EXPERT_MODES.map((mode) => Type.Literal(mode))),
    executionEnvironment: Type.Optional(UserAutomationExecutionEnvironmentSchema)
  },
  { additionalProperties: false }
);

const JsonPrimitiveSchema = z.union([z.string().max(16_384), z.number().finite(), z.boolean(), z.null()]);
const JsonValueSchema: z.ZodType<JsonWireValue> = z.lazy(() => z.union([
  z.string().max(BROWSER_PROGRAM_SOURCE_MAX_CHARS),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(JsonValueSchema).max(2_000),
  z.record(z.string().max(256), JsonValueSchema)
]));
const BoundedJsonValueSchema = JsonValueSchema.superRefine((value, context) => {
  if (JSON.stringify(value).length > 256_000) {
    context.addIssue({ code: "custom", message: "Browser Tool payload exceeds 256000 characters" });
  }
});
export const BrowserEmptyInputSchema = z.object({}).strict();
export const BrowserOpenRequestedSchema = z.object({
  status: z.literal("browser_open_requested"), effect: z.literal("none"), terminal: z.literal(true)
}).strict();
export const BrowserOpenedSchema = z.object({
  status: z.literal("browser_opened"), effect: z.literal("none"), terminal: z.literal(true)
}).strict();
const BrowserVfsPathSchema = z.string().min(2).max(1_024).regex(
  new RegExp(VFS_PATH_PATTERN, "u"),
  { message: "Expected an absolute VFS path without control characters" }
);
const BrowserProgramPathSchema = z.string().min(2).max(1_024).regex(
  new RegExp(PROGRAM_PATH_PATTERN, "u"),
  { message: "Expected an absolute .flow.ts VFS program path without control characters" }
)
  .describe(
    "Absolute VFS path ending in .flow.ts, e.g. \"/input/web.search.flow.ts\" or \"/tmp/output/web.search.flow.ts\". Must start with \"/\"."
  );
const BrowserRecordingSourcePathSchema = BrowserProgramPathSchema.describe(
  "Absolute .flow.ts VFS path of the original recorded Temporary File. Only pass when the current program was edited from a recording."
);
const BrowserSavableProgramPathSchema = z.string().min(2).max(1_024).regex(
  new RegExp(SAVABLE_PROGRAM_PATH_PATTERN, "u"),
  {
    message:
      "Expected an exact .flow.ts candidate path under /input, /working_home, /tmp/files, or /tmp/output."
  }
)
  .describe(
    "Absolute VFS path of the exact .flow.ts program candidate to save. Use /tmp/output for the current edited output, or /input, /working_home, and /tmp/files for existing Temporary File candidates."
);
const BrowserTestPathSchema = BrowserVfsPathSchema.describe(
  "Absolute VFS path of the optional program.test.json file."
);
export const BrowserFrontendToolIdSchema = z.string().min(3).max(128).regex(
  new RegExp(FRONTEND_TOOL_ID_PATTERN, "u"),
  { message: "Expected a saved Frontend Tool ID such as web.search" }
).describe("toolId returned by list_frontend_tools for one saved operation.");
const BrowserCaseNamesSchema = z.array(z.string().min(1).max(256)).max(32).describe(
  "JSON array of exact test case name strings to replay, e.g. [\"search with keyword\"]. Must be an array, never a plain string. Omit entirely to replay every case."
);
export const BrowserRecordedStepEvidenceInputSchema = z.object({
  programPath: BrowserProgramPathSchema.describe(
    "Absolute .flow.ts path of the program whose step evidence is needed."
  ),
  recordingSourcePath: BrowserRecordingSourcePathSchema.describe(
    "Absolute .flow.ts path of the original recorded Temporary File containing the step."
  ),
  stepKey: z.string().min(1).max(256).describe("Recorded stepKey whose sanitized evidence is needed.")
}).strict();
export const BrowserCheckFrontendProgramInputSchema = z.object({
  programPath: BrowserProgramPathSchema.describe(
    "Absolute .flow.ts path to statically validate. Must be an existing Temporary File path or /tmp/output candidate from this invocation."
  ),
  recordingSourcePath: BrowserRecordingSourcePathSchema.optional()
}).strict();
export const BrowserUserInputSchema = z.object({
  question: z.string().trim().min(1).max(500).describe("One concise question for the Human."),
  options: z.array(z.string().trim().min(1).max(160)).min(2).max(4).optional().describe(
    "JSON array of 2–4 mutually exclusive choice strings, e.g. [\"Option A\", \"Option B\"]. Must be an array, never a plain string. Omit when free-form input is better."
  )
}).strict();
export const BrowserSuggestFrontendProgramInputSchema = BrowserUserInputSchema.extend({
  summary: z.string().trim().min(1).max(300).describe(
    "Short proposed reuse scope shown with the question."
  )
});
export const BrowserUserConfirmationInputSchema = z.object({
  purpose: z.enum(["replay", "save"]).describe(
    "\"replay\" to authorize a test replay, or \"save\" to authorize saving the program as a reusable operation."
  ),
  programPath: BrowserProgramPathSchema.describe(
    "Absolute .flow.ts VFS path of the program to confirm. For replay, use the exact path already checked and confirmed. For save, use /tmp/output for a newly edited candidate, or /input, /working_home, /tmp/files for existing candidates."
  ),
  testPath: BrowserTestPathSchema.optional(),
  recordingSourcePath: BrowserRecordingSourcePathSchema.optional(),
  caseNames: z.array(z.string().min(1).max(256)).max(32).optional().describe(
    "Array of exact test case name strings to replay, e.g. [\"default\"]. Must be a JSON array, not a string. Only valid when purpose is \"replay\". Omit entirely to replay every case."
  )
}).strict();
export const BrowserSaveFrontendToolInputSchema = z.object({
  programPath: BrowserSavableProgramPathSchema,
  confirmationId: z.uuid().describe("Single-use confirmationId returned by the accepted save confirmation.")
}).strict();
export const BrowserFinishWithoutExecutionInputSchema = z.object({
  reason: z.enum(["withdrawn", "no_match", "information", "missing_evidence"]).describe(
    "Why this invocation ends without running a saved operation."
  ),
  explanation: z.string().trim().min(1).max(500).describe(
    "Brief user-facing explanation with no execution claim."
  )
}).strict();
const BrowserArgumentsSchema = z.record(z.string().min(1).max(128), JsonPrimitiveSchema)
  .refine((value) => Object.keys(value).length <= 30, { message: "At most 30 arguments are allowed" })
  .describe("Input values keyed exactly by the selected program's input names.");
const BrowserPrimitiveDefinitionSchema = z.object({
  type: z.enum(["string", "number", "boolean"]),
  description: z.string().max(4_096),
  required: z.boolean().optional(),
  example: z.union([z.string().max(16_384), z.number().finite(), z.boolean()]).optional(),
  enum: z.array(z.union([z.string().max(16_384), z.number().finite(), z.boolean()])).max(100).optional()
}).strict();
export const BrowserFrontendToolListSchema = z.array(z.object({
  toolId: BrowserFrontendToolIdSchema,
  status: z.literal("valid"),
  name: z.string().min(1).max(128),
  description: z.string().max(4_096),
  inputs: z.record(z.string().max(128), BrowserPrimitiveDefinitionSchema),
  outputs: z.record(z.string().max(128), BrowserPrimitiveDefinitionSchema),
  websites: z.array(z.string().max(2_048)).max(32),
  effect: z.enum(["read", "write"]),
  diagnostics: z.array(z.object({
    code: z.string().min(1).max(128),
    message: z.string().min(1).max(2_048)
  }).strict()).max(0),
  verification: z.literal("not_retained")
}).strict().or(z.object({
  toolId: BrowserFrontendToolIdSchema.optional(),
  status: z.literal("invalid"),
  name: z.string().min(1).max(128).optional(),
  diagnostics: z.array(z.object({
    code: z.string().min(1).max(128),
    message: z.string().min(1).max(2_048)
  }).strict()).min(1).max(64),
  verification: z.literal("not_retained")
}).strict())).max(200);
export const BrowserProgramReviewSchema = z.object({
  title: z.string().max(4_096),
  description: z.string().max(4_096),
  targetFileName: z.string().min(1).max(256),
  replacesSavedTool: z.boolean(),
  websites: z.array(z.string().max(2_048)).max(32),
  effect: z.enum(["read", "write"]),
  stepCount: z.number().int().positive(),
  inputs: z.array(z.object({
    name: z.string().min(1).max(128),
    definition: BrowserPrimitiveDefinitionSchema
  }).strict()).max(30),
  fixedValues: z.array(z.object({
    stepKey: z.string().min(1).max(256),
    operation: z.string().min(1).max(64),
    value: z.string().max(16_384)
  }).strict()).max(256),
  staticDiagnostics: z.array(z.object({
    code: z.string().min(1).max(128),
    message: z.string().min(1).max(2_048)
  }).strict()).max(64),
  testStatus: z.enum(["not_run", "partial", "passed", "failed"]),
  testedCases: z.array(z.string().min(1).max(256)).max(32),
  observedResult: z.string().trim().min(1).max(1_024).optional()
}).strict();
export type BrowserProgramReviewV1 = z.infer<typeof BrowserProgramReviewSchema>;

export const BrowserHumanInteractionSchema = z.object({
  id: z.uuid(),
  tool: z.enum(["suggest_frontend_program", "request_user_confirmation", "request_user_input"]),
  purpose: z.enum(["replay", "save", "execute", "delete"]).optional(),
  summary: z.string().max(4_000),
  review: BrowserProgramReviewSchema.optional(),
  question: z.string().min(1).max(500),
  options: z.array(z.string().min(1).max(160)).min(2).max(4).optional(),
  expiresAt: z.number().int().positive(),
  execution: z.object({
    name: z.string().min(1).max(128),
    description: z.string().max(4_096),
    inputs: z.record(z.string().max(128), BrowserPrimitiveDefinitionSchema),
    arguments: BrowserArgumentsSchema,
    websites: z.array(z.string().max(2_048)).max(32),
    effect: z.enum(["read", "write"])
  }).strict().optional(),
  deletion: z.object({
    items: z.array(z.object({
      name: z.string().min(1).max(128),
      description: z.string().max(4_096),
      websites: z.array(z.string().max(2_048)).max(32),
      valid: z.boolean()
    }).strict()).min(1).max(200)
  }).strict().optional()
}).strict();
export const BrowserHumanAnswerSchema = z.object({
  taskId: z.uuid(),
  interactionId: z.uuid(),
  decision: z.enum(["accepted", "revised", "rejected"]),
  text: z.string().trim().min(1).max(4_000).optional(),
  arguments: BrowserArgumentsSchema.optional()
}).strict();
export type BrowserHumanInteractionV1 = z.infer<typeof BrowserHumanInteractionSchema>;
export type BrowserHumanAnswerV1 = z.infer<typeof BrowserHumanAnswerSchema>;
export const BrowserTestFrontendProgramInputSchema = z.object({
  programPath: BrowserProgramPathSchema.describe(
    "Absolute .flow.ts path of the exact program to replay. Must be the same path already checked and confirmed."
  ),
  testPath: BrowserTestPathSchema.optional(),
  recordingSourcePath: BrowserRecordingSourcePathSchema.optional(),
  caseNames: BrowserCaseNamesSchema.optional()
}).strict();
export const BrowserExecuteFrontendToolInputSchema = z.object({
  toolId: BrowserFrontendToolIdSchema.describe(
    "toolId returned by list_frontend_tools for one valid saved operation."
  ),
  arguments: BrowserArgumentsSchema
}).strict();
export const BrowserDeleteFrontendToolInputSchema = z.object({
  toolIds: z.array(BrowserFrontendToolIdSchema).min(1).max(200).describe(
    "One or more toolIds returned by list_frontend_tools to delete after a single Human confirmation."
  )
}).strict();
export const BrowserReadFrontendToolInputSchema = z.object({
  toolId: BrowserFrontendToolIdSchema.describe(
    "toolId returned by list_frontend_tools for one saved operation whose current local DSL should be read."
  )
}).strict();

export const BrowserFrontendToolCommandSchema = z.discriminatedUnion("tool", [
  z.object({ tool: z.literal("open_browser"), input: BrowserEmptyInputSchema }).strict(),
  z.object({ tool: z.literal("read_recorded_step_evidence"), input: BrowserRecordedStepEvidenceInputSchema }).strict(),
  z.object({ tool: z.literal("suggest_frontend_program"), input: BrowserSuggestFrontendProgramInputSchema }).strict(),
  z.object({ tool: z.literal("request_user_confirmation"), input: BrowserUserConfirmationInputSchema }).strict(),
  z.object({ tool: z.literal("save_frontend_tool"), input: BrowserSaveFrontendToolInputSchema }).strict(),
  z.object({ tool: z.literal("request_user_input"), input: BrowserUserInputSchema }).strict(),
  z.object({ tool: z.literal("finish_without_execution"), input: BrowserFinishWithoutExecutionInputSchema }).strict(),
  z.object({ tool: z.literal("check_frontend_program"), input: BrowserCheckFrontendProgramInputSchema }).strict(),
  z.object({
    tool: z.literal("test_frontend_program"),
    input: BrowserTestFrontendProgramInputSchema
  }).strict(),
  z.object({ tool: z.literal("list_frontend_tools"), input: BrowserEmptyInputSchema }).strict(),
  z.object({
    tool: z.literal("execute_frontend_tool"),
    input: BrowserExecuteFrontendToolInputSchema
  }).strict(),
  z.object({
    tool: z.literal("delete_frontend_tool"),
    input: BrowserDeleteFrontendToolInputSchema
  }).strict(),
  z.object({
    tool: z.literal("read_frontend_tool"),
    input: BrowserReadFrontendToolInputSchema
  }).strict()
]);

const BrowserTemporaryFileSourceSchema = z.object({
  kind: z.literal("temporary_file"),
  temporaryFileId: z.uuid(),
  contentSha256: z.string().regex(/^[a-f0-9]{64}$/u),
  expiresAt: z.iso.datetime()
}).strict();
const BrowserInvocationOutputSourceSchema = z.object({
  kind: z.literal("invocation_output"),
  path: z.string().min(2).max(1_024).regex(
    /^\/tmp\/output\/[^\u0000-\u001F\u007F\\]+\.flow\.ts$/u
  ),
  contentSha256: z.string().regex(/^[a-f0-9]{64}$/u)
}).strict();
const BrowserProgramFilePayloadSchema = z.object({
  path: BrowserVfsPathSchema,
  fileName: z.string().min(1).max(512),
  mediaType: z.string().min(1).max(256).nullable(),
  sourceText: z.string().max(BROWSER_PROGRAM_SOURCE_MAX_CHARS),
  sizeBytes: z.number().int().positive().max(BROWSER_PROGRAM_SOURCE_MAX_CHARS),
  contentSha256: z.string().regex(/^[a-f0-9]{64}$/u),
  source: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("document"),
      documentId: z.uuid(),
      representation: z.enum(["source", "canonical_markdown"]),
      version: z.string().min(1).max(256)
    }).strict(),
    BrowserTemporaryFileSourceSchema,
    BrowserInvocationOutputSourceSchema
  ]).optional()
}).strict();
const BrowserTemporaryProgramFilePayloadSchema = BrowserProgramFilePayloadSchema.extend({
  source: BrowserTemporaryFileSourceSchema
}).strict();
const BrowserSavableProgramFilePayloadSchema = BrowserProgramFilePayloadSchema.extend({
  source: z.discriminatedUnion("kind", [
    BrowserTemporaryFileSourceSchema,
    BrowserInvocationOutputSourceSchema
  ])
}).strict();
const BrowserInternalConfirmationInputSchema = z.discriminatedUnion("purpose", [
  z.object({
    purpose: z.literal("replay"),
    program: BrowserProgramFilePayloadSchema,
    test: BrowserProgramFilePayloadSchema.optional(),
    recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional(),
    caseNames: z.array(z.string().min(1).max(256)).max(32).optional()
  }).strict(),
  z.object({
    purpose: z.literal("save"),
    program: BrowserSavableProgramFilePayloadSchema,
    test: BrowserProgramFilePayloadSchema.optional(),
    recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional()
  }).strict()
]);

export const BrowserFrontendToolRequestSchema = z.discriminatedUnion("tool", [
  z.object({ tool: z.literal("open_browser"), input: BrowserEmptyInputSchema }).strict(),
  z.object({ tool: z.literal("read_recorded_step_evidence"), input: z.object({
    program: BrowserProgramFilePayloadSchema,
    recordingProgram: BrowserTemporaryProgramFilePayloadSchema,
    stepKey: z.string().min(1).max(256)
  }).strict() }).strict(),
  z.object({ tool: z.literal("suggest_frontend_program"), input: BrowserSuggestFrontendProgramInputSchema }).strict(),
  z.object({ tool: z.literal("request_user_confirmation"), input: BrowserInternalConfirmationInputSchema }).strict(),
  z.object({ tool: z.literal("save_frontend_tool"), input: z.object({
    program: BrowserSavableProgramFilePayloadSchema,
    confirmationId: z.uuid()
  }).strict() }).strict(),
  z.object({ tool: z.literal("request_user_input"), input: BrowserUserInputSchema }).strict(),
  z.object({ tool: z.literal("finish_without_execution"), input: BrowserFinishWithoutExecutionInputSchema }).strict(),
  z.object({ tool: z.literal("check_frontend_program"), input: z.object({
    program: BrowserProgramFilePayloadSchema,
    recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional()
  }).strict() }).strict(),
  z.object({ tool: z.literal("test_frontend_program"), input: z.object({
    program: BrowserProgramFilePayloadSchema,
    test: BrowserProgramFilePayloadSchema.optional(),
    recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional(),
    caseNames: z.array(z.string().min(1).max(256)).max(32).optional()
  }).strict() }).strict(),
  z.object({ tool: z.literal("list_frontend_tools"), input: BrowserEmptyInputSchema }).strict(),
  z.object({ tool: z.literal("execute_frontend_tool"), input: z.object({
    toolId: BrowserFrontendToolIdSchema,
    arguments: BrowserArgumentsSchema
  }).strict() }).strict(),
  z.object({ tool: z.literal("delete_frontend_tool"), input: z.object({
    toolIds: z.array(BrowserFrontendToolIdSchema).min(1).max(200)
  }).strict() }).strict(),
  z.object({ tool: z.literal("read_frontend_tool"), input: z.object({
    toolId: BrowserFrontendToolIdSchema
  }).strict() }).strict()
]);

export const BrowserToolRpcResultSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), value: BoundedJsonValueSchema }).strict(),
  z.object({
    ok: z.literal(false),
    error: z.object({
      code: z.string().min(1).max(128).regex(/^[A-Z][A-Z0-9_]*$/u),
      message: z.string().min(1).max(2_048),
      effect: z.enum(["none", "possible", "confirmed"])
    }).strict()
  }).strict()
]);

export const FrontendToolProbeSchema = z.object({
  roomId: BrowserControlRoomId,
  sourceEventId: SourceEventId,
  mode: BrowserExpertModeSchema,
  replyTopic: BrowserControlReplyTopic
}).strict();
export const FrontendToolReadySchema = z.object({
  roomId: BrowserControlRoomId,
  sourceEventId: SourceEventId,
  mode: BrowserExpertModeSchema,
  executorId: InvocationId
}).strict();
const FrontendToolRequestEnvelope = {
  roomId: BrowserControlRoomId,
  sourceEventId: SourceEventId,
  executorId: InvocationId,
  requestId: InvocationId
};
export const FrontendToolRequestSchema = z.discriminatedUnion("tool", [
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("open_browser"), input: BrowserEmptyInputSchema }).strict(),
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("suggest_frontend_program"), input: BrowserSuggestFrontendProgramInputSchema }).strict(),
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("request_user_confirmation"), input: BrowserInternalConfirmationInputSchema }).strict(),
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("save_frontend_tool"), input: z.object({
    program: BrowserSavableProgramFilePayloadSchema,
    confirmationId: z.uuid()
  }).strict() }).strict(),
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("request_user_input"), input: BrowserUserInputSchema }).strict(),
  z.object({ ...FrontendToolRequestEnvelope, tool: z.literal("finish_without_execution"), input: BrowserFinishWithoutExecutionInputSchema }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("read_recorded_step_evidence"),
    input: z.object({
      program: BrowserProgramFilePayloadSchema,
      recordingProgram: BrowserTemporaryProgramFilePayloadSchema,
      stepKey: z.string().min(1).max(256)
    }).strict()
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("check_frontend_program"),
    input: z.object({
      program: BrowserProgramFilePayloadSchema,
      recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional()
    }).strict()
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("test_frontend_program"),
    input: z.object({
      program: BrowserProgramFilePayloadSchema,
      test: BrowserProgramFilePayloadSchema.optional(),
      recordingProgram: BrowserTemporaryProgramFilePayloadSchema.optional(),
      caseNames: z.array(z.string().min(1).max(256)).max(32).optional()
    }).strict()
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("list_frontend_tools"),
    input: BrowserEmptyInputSchema
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("execute_frontend_tool"),
    input: z.object({
      toolId: BrowserFrontendToolIdSchema,
      arguments: BrowserArgumentsSchema
    }).strict()
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("delete_frontend_tool"),
    input: z.object({
      toolIds: z.array(BrowserFrontendToolIdSchema).min(1).max(200)
    }).strict()
  }).strict(),
  z.object({
    ...FrontendToolRequestEnvelope,
    tool: z.literal("read_frontend_tool"),
    input: z.object({
      toolId: BrowserFrontendToolIdSchema
    }).strict()
  }).strict()
]);
export const FrontendToolResponseSchema = z.object({
  roomId: BrowserControlRoomId,
  sourceEventId: SourceEventId,
  executorId: InvocationId,
  requestId: InvocationId,
  result: BrowserToolRpcResultSchema
}).strict();
export const FrontendToolCancelSchema = z.object({
  roomId: BrowserControlRoomId,
  sourceEventId: SourceEventId,
  executorId: InvocationId,
  requestId: InvocationId
}).strict();
export const BrowserToolRealtimeRelayRequestSchema = z.discriminatedUnion("event", [
  z.object({
    topic: BrowserControlReplyTopic,
    event: z.literal(FRONTEND_TOOL_REALTIME_EVENTS.ready),
    payload: FrontendToolReadySchema
  }).strict(),
  z.object({
    topic: BrowserControlReplyTopic,
    event: z.literal(FRONTEND_TOOL_REALTIME_EVENTS.response),
    payload: FrontendToolResponseSchema
  }).strict()
]);

export function browserControlDeviceTopic(deviceId: string): string {
  const parsed = z.uuid().parse(deviceId);
  return `${BROWSER_CONTROL_DEVICE_TOPIC_PREFIX}${parsed}`;
}

export function browserControlAgentTopic(instanceId: string): string {
  const parsed = z.uuid().parse(instanceId);
  return `${BROWSER_CONTROL_AGENT_TOPIC_PREFIX}${parsed}`;
}

export function browserControlTopic(value: string): string {
  const parsed = z.string().min(1).max(512).regex(/^browser-control:(?:device|agent):\S+$/u).parse(value);
  return parsed;
}

const BrowserExpertActivityEventBase = {
  version: Type.Literal("1.0"),
  sourceEventId: MatrixEventIdSchema,
  sequence: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
  text: Type.String({ minLength: 1, maxLength: 256 })
};
export const BrowserExpertActivityPhaseSchema = Type.Union([
  Type.Literal("waiting_human"),
  Type.Literal("connecting_browser"),
  Type.Literal("reading_program"),
  Type.Literal("editing_program"),
  Type.Literal("testing_program"),
  Type.Literal("repairing_target"),
  Type.Literal("executing_tool"),
  Type.Literal("finalizing")
]);
export const BrowserExpertProgressEventSchema = Type.Object({
  ...BrowserExpertActivityEventBase,
  type: Type.Literal("progress"),
  phase: BrowserExpertActivityPhaseSchema
}, { additionalProperties: false });
export const BrowserExpertToolEventSchema = Type.Object({
  ...BrowserExpertActivityEventBase,
  type: Type.Literal("tool"),
  activityId: Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$" }),
  tool: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z][A-Za-z0-9_]*$" }),
  status: Type.Union([
    Type.Literal("selected"),
    Type.Literal("started"),
    Type.Literal("completed"),
    Type.Literal("failed")
  ]),
  detail: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
  errorCode: Type.Optional(Type.String({
    minLength: 3,
    maxLength: 128,
    pattern: "^[A-Z][A-Z0-9_]{0,127}$"
  })),
  durationMs: Type.Optional(Type.Integer({ minimum: 0, maximum: 300_000 }))
}, { additionalProperties: false });
export const BrowserExpertActivityEventSchema = Type.Union([
  BrowserExpertProgressEventSchema,
  BrowserExpertToolEventSchema
]);
export const BrowserExpertActivitySummarySchema = Type.Object({
  events: Type.Array(BrowserExpertActivityEventSchema, { maxItems: 64 })
}, { additionalProperties: false });

export type BrowserExpertModeV1 = z.infer<typeof BrowserExpertModeSchema>;
export type BrowserExpertInvocationContentV1 = Static<
  typeof BrowserExpertInvocationContentSchema
>;
export type BrowserFrontendToolCommandV1 = z.infer<typeof BrowserFrontendToolCommandSchema>;
export type BrowserFrontendToolRequestV1 = z.infer<typeof BrowserFrontendToolRequestSchema>;
export type BrowserFrontendToolListV1 = z.infer<typeof BrowserFrontendToolListSchema>;
export type BrowserProgramFilePayloadV1 = z.infer<typeof BrowserProgramFilePayloadSchema>;
export type BrowserToolRpcResultV1 = z.infer<typeof BrowserToolRpcResultSchema>;
export type FrontendToolProbeV1 = z.infer<typeof FrontendToolProbeSchema>;
export type FrontendToolReadyV1 = z.infer<typeof FrontendToolReadySchema>;
export type FrontendToolRequestV1 = z.infer<typeof FrontendToolRequestSchema>;
export type FrontendToolResponseV1 = z.infer<typeof FrontendToolResponseSchema>;
export type FrontendToolCancelV1 = z.infer<typeof FrontendToolCancelSchema>;
export type BrowserToolRealtimeRelayRequestV1 = z.infer<typeof BrowserToolRealtimeRelayRequestSchema>;
export type BrowserExpertProgressEventV1 = Static<typeof BrowserExpertProgressEventSchema>;
export type BrowserExpertActivityPhaseV1 = Static<typeof BrowserExpertActivityPhaseSchema>;
export type BrowserExpertToolEventV1 = Static<typeof BrowserExpertToolEventSchema>;
export type BrowserExpertActivityEventV1 = Static<typeof BrowserExpertActivityEventSchema>;
export type BrowserExpertActivitySummaryV1 = Static<typeof BrowserExpertActivitySummarySchema>;
